import React, { useState, useId } from "react";
import styled from "@emotion/styled";
import { text, surface, interactive, controlFontSize, spacing, duration, mediaBelow } from "@atlas-int/tokens";

const Wrapper = styled.div`
  border: 1px solid ${interactive.muted};
  padding: ${spacing[4]};
  background-color: ${surface.panel};

  ${mediaBelow("md")} {
    border: none;
    background-color: transparent;
  }
`;

const ToggleButton = styled.button`
  font-family: inherit;
  cursor: pointer;
  background: none;
  border: none;
  color: ${text.primary};
  font-size: ${controlFontSize.default};
  padding: 0;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;

  &::before {
    content: "";
    display: inline-block;
    width: 0;
    height: 0;
    flex-shrink: 0;
    margin-right: 0.4rem;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 6px solid currentColor;
    transition: transform ${duration.fast} ease;
  }

  &[data-open="true"]::before {
    transform: rotate(90deg);
  }

  ${mediaBelow("md")} {
    align-items: flex-start;

    &::before {
      margin-top: 0.5rem;
    }
  }
`;

const Content = styled.div`
  padding-top: ${spacing[4]};

  a {
    color: ${text.content};

    &:hover {
      text-decoration: underline;
    }
  }
`;

// ─── HTML sanitizer ───────────────────────────────────────────────────────────

const ALLOWED_TAGS = new Set([
  "a", "b", "br", "code", "div", "em", "h1", "h2", "h3", "h4", "h5", "h6",
  "i", "li", "ol", "p", "pre", "small", "span", "strong", "sub", "sup",
  "table", "tbody", "td", "th", "thead", "tr", "u", "ul",
]);

const ALLOWED_ATTRS = new Set(["href", "target", "rel", "class", "id"]);

function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
          el.replaceWith(document.createTextNode(el.textContent || ""));
          continue;
        }
        for (const attr of Array.from(el.attributes)) {
          if (!ALLOWED_ATTRS.has(attr.name)) {
            el.removeAttribute(attr.name);
          }
        }
        if (el.tagName === "A") {
          const href = el.getAttribute("href") || "";
          if (href.startsWith("javascript:")) {
            el.setAttribute("href", "#");
          }
        }
        walk(el);
      }
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

export interface CollapsibleSectionProps {
  /** The clickable label text */
  label: string;
  /**
   * Content shown when expanded.
   * - Pass a `ReactNode` (JSX) for safe rendering (preferred).
   * - Pass a `string` for backward-compatible HTML rendering.
   *   HTML strings are sanitized to allow only safe markup.
   */
  children: string | React.ReactNode;
}

const CollapsibleSection = ({ label, children }: CollapsibleSectionProps) => {
  const [open, setOpen] = useState(false);
  const contentId = useId();

  return (
    <Wrapper>
      <ToggleButton
        aria-expanded={open}
        aria-controls={contentId}
        data-open={open}
        onClick={() => setOpen((s) => !s)}
      >
        <span>{label}</span>
      </ToggleButton>

      {open && (
        <Content id={contentId} role="region">
          {typeof children === "string" ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(children) }} />
          ) : (
            children
          )}
        </Content>
      )}
    </Wrapper>
  );
};

export default CollapsibleSection;
